import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import { Form, Input, Select, Button, Switch, Flex, Row, Col } from "antd"
import { CloseCircleOutlined, DeleteOutlined, PlusCircleFilled } from "@ant-design/icons"
import styles from "./index.module.css"
import { Text } from "ui/Typography"
import { saveQuestionnaireTemplateQuestions, updateQuestionnaireTemplateQuestions } from "store/question"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import IconWithBg from "ui/IconWithBg"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
const questionTypes = [
  { value: "Paragraph", label: "Paragraph" },
  { value: "Dropdown", label: "Dropdown" },
  { value: "Checkbox", label: "Checkbox" },
  { value: "Radio", label: "Radio" }
]

const defaultOptions = [
  { label: "Option A", value: "" },
  { label: "Option B", value: "" },
  { label: "Option C", value: "" },
  { label: "Option D", value: "" }
]

// Generate a simple unique ID for new questions
const generateId = () => Math.random().toString(36).substring(2, 11)

const defaultQuestion = () => ({
  id: `q_${generateId()}`,
  question: "",
  type: "Paragraph",
  required: true,
  answer: "",
  options: structuredClone(defaultOptions)
})

const mapApiQuestionToLocal = (q) => ({
  id: q.id,
  question: q.question_text || "",
  type: q.question_type ? q.question_type.charAt(0).toUpperCase() + q.question_type.slice(1) : "Paragraph",
  required: q.required ?? true,
  answer: "", // You can map answer if you have it in API
  options: (q.questions_options || []).map((opt, idx) => ({
    id: opt.id,
    label: `Option ${String.fromCodePoint(65 + idx)}`,
    value: opt.option_text || ""
  }))
})

const CustomQuestions = ({ templateId, templateQuestions }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [questions, setQuestions] = useState([defaultQuestion()])
  const [status, setStatus] = useState("")
 const {loading} = useSelector((state) => state.question)
const navigate = useNavigate()
  useEffect(() => {
    if (Array.isArray(templateQuestions) && templateQuestions.length > 0) {
      setQuestions(templateQuestions.map(mapApiQuestionToLocal))
    }
  }, [templateQuestions])

  const addQuestion = () => {
    setQuestions([...questions, defaultQuestion()])
  }

  const removeQuestion = (id) => {
    if (questions.length > 1) {
      setQuestions(
        questions
          .map((q) => {
            if (q.id === id) {
              // If question has id, mark _destroy, else remove
              if (q.id) return { ...q, _destroy: true }
              return null
            }
            return q
          })
          .filter(Boolean)
      )
    }
  }

  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, [field]: value } : q)))
  }

  const updateOption = (questionId, filteredIndex, value) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          // Get the actual index in the options array, considering deleted options
          const activeOptions = q.options.filter(opt => !opt._destroy)
          if (filteredIndex < 0 || filteredIndex >= activeOptions.length) return q
          
          const optionToUpdate = activeOptions[filteredIndex]
          const actualIndex = q.options.indexOf(optionToUpdate)
          
          if (actualIndex === -1) return q
          
          // Create a new options array with the updated value
          const newOptions = [...q.options]
          newOptions[actualIndex] = { 
            ...newOptions[actualIndex], 
            value 
          }
          
          return { 
            ...q, 
            options: newOptions,
            _updated: Date.now() // Force re-render
          }
        }
        return q
      })
    )
  }

  const addOption = (questionId) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          // Only count non-deleted options for the next label
          const activeOptions = q.options.filter(opt => !opt._destroy)
          const nextLabel = String.fromCodePoint(65 + activeOptions.length)
          
          return {
            ...q,
            options: [...q.options, { label: `Option ${nextLabel}`, value: "" }],
            // Add a timestamp to force re-render of the options
            _updated: Date.now()
          }
        }
        return q
      })
    )
  }

  const removeOption = (questionId, filteredIndex) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          // Create a new array with only non-deleted options to find the actual index
          const activeOptions = q.options.filter(opt => !opt._destroy)
          
          // If the index is invalid, return the question as is
          if (filteredIndex < 0 || filteredIndex >= activeOptions.length) {
            return q
          }
          
          // Find the actual index in the original options array
          const optionToRemove = activeOptions[filteredIndex]
          const actualIndex = q.options.indexOf(optionToRemove)
          
          if (actualIndex === -1) return q // Not found, return as is

          // Create a new options array with the option marked for deletion or removed
          const newOptions = [...q.options]
          if (optionToRemove.id) {
            // For existing options, mark for deletion
            newOptions[actualIndex] = { ...optionToRemove, _destroy: true }
          } else {
            // For new options, remove completely
            newOptions.splice(actualIndex, 1)
          }

          // Return a new question object to ensure re-render
          return { 
            ...q, 
            options: newOptions,
            // Add a timestamp to force re-render of the input field
            _updated: Date.now() 
          }
        }
        return q
      })
    )
  }
  const validateQuestions = () => {
    let isValid = true
    const requiredFields = []

    questions.forEach((q, index) => {
      if (q._destroy) return

      if (q.required && !q.question.trim()) {
        requiredFields.push(`Question ${index + 1}`)
        isValid = false
      }

      if (["Dropdown", "Checkbox", "Radio"].includes(q.type)) {
        q.options.forEach((opt, optIndex) => {
          if (!opt._destroy && !opt.value.trim()) {
            requiredFields.push(`Option ${String.fromCodePoint(65 + optIndex)} in Question ${index + 1}`)
            isValid = false
          }
        })
      }
    })

    if (!isValid) {
      toaster.error(`Please fill in all required fields: ${requiredFields.join(", ")}`)
    }

    return isValid
  }

  const handleSubmit = async (status) => {
    setStatus(status)
    try {
      // Validate required fields before submission (only for non-deleted questions)
      if (!validateQuestions()) {
        return
      }

      const formattedQuestions = questions.map((q) => {
        // For deleted questions, only include id and _destroy flag
        if (q._destroy) {
          return {
            id: q.id,
            _destroy: true
          }
        }

        // For active questions, include all the question data
        let questionData = {
          question_text: q.question,
          question_type: q.type.toLowerCase(),
          required: q.required,
          questions_options_attributes:
            q.type === "Paragraph"
              ? []
              : q.options.map((opt) => ({
                  ...(opt.id && { id: opt.id }),
                  ...(opt._destroy && { _destroy: true }),
                  ...(!opt._destroy && { option_text: opt.value })
                }))
        }
        
        // Include the ID for existing questions (not for new ones with temporary 'q_' prefix)
        if (
          (q.id && typeof q.id === "number") ||
          (q.id && typeof q.id === "string" && !q.id.startsWith("q_"))
        ) {
          questionData.id = q.id
        }
        
        return questionData
      })

      const payload = {
        questions: formattedQuestions,
        questionnaire_template: { status }
      }

      console.log("Submitting payload:", payload)

      let res
      if (templateQuestions && templateQuestions.length > 0) {
        res = await dispatch(updateQuestionnaireTemplateQuestions(templateId, payload))
        if (res.message) {
          toaster.success(res.message)
          navigate(routes.proSettingsCustomTemplate())
        }
      } else {
        res = await dispatch(saveQuestionnaireTemplateQuestions(templateId, payload))
        if (res.message) {
          toaster.success(res.message)
         navigate(routes.proSettingsCustomTemplate())

        }
      }
    } catch (error) {
      console.error("Error saving questions:", error)
    }
  }

  return (
    <div className={styles.container}>
      <Form layout="vertical">
        {questions
          .filter((q) => !q._destroy)
          .map((q, idx) => (
            <div key={q.id} className={styles.questionCard}>
              <Flex align="flex-start" justify="space-between" gap={24}>
                <Row gutter={[16, 16]}>
                  <Col span={18}>
                    <Form.Item label={`Question ${idx + 1}`} className={styles.formItem}>
                      <Input
                        placeholder="What is your billing address?"
                        value={q.question}
                        onChange={(e) => updateQuestion(q.id, "question", e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Question type" className={styles.formItem}>
                      <Select
                        value={q.type}
                        style={{ width: 160 }}
                        onChange={(val) => updateQuestion(q.id, "type", val)}
                        options={questionTypes}
                      />
                    </Form.Item>
                  </Col>
                  {/* Render answer/options based on type */}
                  {q.type === "Paragraph" && (
                    <Col span={24}>
                      <Form.Item label="Answer" style={{ marginBottom: 0 }}>
                        <Input.TextArea
                          placeholder="Enter text here"
                          autoSize={{ minRows: 4 }}
                          value={q.answer}
                          disabled={true}
                          onChange={(e) => updateQuestion(q.id, "answer", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {(q.type === "Dropdown" || q.type === "Checkbox" || q.type === "Radio") && (
                    <Col span={12}>
                      {q.options
                        .filter((opt) => !opt._destroy)
                        .map((opt, optIdx) => (
                          <div key={opt.id || `${opt.label}-${optIdx}`} className={styles.optionItem}>
                            <div className={styles.optionLabel}>{`Option ${String.fromCodePoint(65 + optIdx)}`}</div>
                            <div className={styles.optionInputContainer}>
                              <Input
                                placeholder="Enter text here"
                                value={opt.value}
                                onChange={(e) => updateOption(q.id, optIdx, e.target.value)}
                                className={`${styles.optionInput} ${styles.optionInputField}`}
                              />
                              <Button
                                type="text"
                                icon={<CloseCircleOutlined className={styles.removeOptionButton} />}
                                onClick={() => removeOption(q.id, optIdx)}
                                className={styles.removeOptionBtn}
                              />
                            </div>
                          </div>
                        ))}
                      <Button type="link" icon={<PlusCircleFilled />} onClick={() => addOption(q.id)} className={styles.addOptionButton}>
                        {t("settings.add_option")}
                      </Button>
                    </Col>
                  )}
                  <Col span={24}>
                    <div className={styles.requiredToggle}>
                      <Flex gap={10} align="center" className={styles.deleteQuestion}>
                        <Text>{t("settings.delete")}</Text>
                        <IconWithBg icon={<DeleteOutlined onClick={() => removeQuestion(q.id)} />} />
                      </Flex>
                      <span className={styles.requiredLabel}>{t("settings.required")}</span>
                      <Switch checked={q.required} onChange={(checked) => updateQuestion(q.id, "required", checked)} />
                    </div>
                  </Col>
                </Row>
              </Flex>
            </div>
          ))}

        <div className={styles.footerActions}>
          <button
            type="button"
            className={styles.addQuestionContainer}
            onClick={addQuestion}
            aria-label={t("settings.add_new_question")}
          >
            <PlusCircleFilled className={styles.addQuestionIcon} />
            <Text className={styles.addQuestionText}>{t("settings.add_new_question")}</Text>
          </button>
          <div className={styles.buttonGroup}>
            <Button color="primary" disabled={loading && status === "draft"} loading={loading && status === "draft"}  variant="outlined" onClick={() => handleSubmit("draft")}>
              {t("settings.save_draft_and_exit")}
            </Button>
            <Button color="primary" disabled={loading && status === "published"} loading={loading && status === "published"}  variant="solid" onClick={() => handleSubmit("published")}>
              {t("settings.next")}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  )
}

export default CustomQuestions

CustomQuestions.propTypes = {
  templateId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  templateQuestions: PropTypes.arrayOf(PropTypes.object)
}
